/**
 * Player profile — per-userId persistent state on staging.
 *
 * Reads the `Player` entity by `userId` at boot (PSV-1898: via a one-shot
 * keyed DataSubscription — `module_dataflow.DataGetRequest` was removed
 * platform-wide, and the new platform pushes current state as the
 * subscription's initial Overwrite). The local cache seeds the HUD; the
 * SERVER is authoritative for every field and persists over its uplink
 * data channel, so the client never writes Player rows any more — a
 * first-time visitor's row is created by the server on first spawn.
 *
 * Schema is hand-written to mirror the staging admin panel. Replace with
 * codegen output once SDK M6 lands (PSV-1271 backlog).
 *
 * Wire shape — Player has `$ref` parts (`Ship` → `parts/Ship`, `Stats` →
 * `parts/BattleStats`), so the query uses nested `.with()` selection rather
 * than flat fields. A flat `Player(id: $v1) { Ship Stats State }` query
 * hangs the backend instead of returning structured data — confirmed by
 * the diagnostic chain in REPORT §1.2 (record-id probe → nested-shape
 * follow-up). MatchConfig got away with flat selection because it has no
 * `$ref` properties.
 *
 * Resilience — if the subscription fails or no push lands within the
 * bounded window (missing row = first-time visitor, or a backend blip),
 * the local cache falls back to DEFAULT_PLAYER so the Arena doesn't get
 * blocked; the server's GameState snapshot then syncs the live values.
 */

import type { ListenHandle, PlayServClient } from 'playserv-sdk';

export interface Player {
  Ship: { Class: string; Weapon: string };
  Stats: { Kills: number; Deaths: number };
  /**
   * Server-managed session metadata (PSV-1393 Stage 7 Track B).
   * On PlayerJoined the server sets FirstSeenAt (if blank), updates
   * LastSeenAt = now, increments SessionsCount. Client treats these
   * as read-only authoritative — never writes them in `persistPlayer`
   * paths (other than the initial defaults round-trip).
   */
  Profile: { FirstSeenAt: string; LastSeenAt: string; SessionsCount: number };
  State: string;
  /** PSV-1337 — gem balance (root-level field, not nested in Stats). */
  Gems: number;
  /** PSV-1337 — JSON-encoded `Array<{type, expiresAt}>` of active buffs. */
  ActiveBuffs: string;
  /**
   * PSV-1338 — linear upgrade tier (0-4). Drives Ship.{HpMax, WeaponDmg,
   * engineMax} via the shared `UPGRADE_TABLE`. Server is authoritative
   * for advancement; client reads + renders the HUD.
   */
  upgradeTier: number;
  /**
   * PSV-1727 — JSON-encoded per-stat levels (shared/upgradeStats
   * StatLevels). Server authoritative via UpgradeStat; client parses
   * with the tolerant `parseStatLevels` (garbage → all zeros).
   */
  statLevels: string;
  /**
   * PSV-1888 — JSON-encoded CosmeticsState (owned + equipped slots).
   * Server authoritative via Purchase/EquipCosmetic; tolerant parse via
   * shared `parseCosmeticsField`.
   */
  Cosmetics: string;
}

const DEFAULT_PLAYER: Player = {
  Ship: { Class: 'Scout', Weapon: 'Laser' },
  Stats: { Kills: 0, Deaths: 0 },
  // First-time visitor bootstrap. Empty timestamps + 0 sessions —
  // the server's PlayerJoined handler will immediately overwrite
  // these with real values, so blank defaults here just prevent
  // type errors during the brief pre-mutation window.
  Profile: { FirstSeenAt: '', LastSeenAt: '', SessionsCount: 0 },
  State: 'Active',
  Gems: 0,
  ActiveBuffs: '',
  // PSV-1338 — start every fresh visitor at tier 0; UPGRADE_TABLE row 0
  // matches the schema defaults (Hp 100 / Dmg 10 / engineMax 200).
  upgradeTier: 0,
  // PSV-1727 — empty blob = level 0 everywhere (parseStatLevels default).
  statLevels: '',
  // PSV-1888 — empty blob = nothing owned/equipped.
  Cosmetics: '',
};

const SHIP_FIELDS: readonly string[] = ['Class', 'Weapon'];
const STATS_FIELDS: readonly string[] = ['Kills', 'Deaths'];
const PROFILE_FIELDS: readonly string[] = [
  'FirstSeenAt',
  'LastSeenAt',
  'SessionsCount',
];
// Stage 7 + PSV-1337 + PSV-1338 merge: scalar field set includes
// Stage 7's `State`, PSV-1337's `Gems` + `ActiveBuffs`, and PSV-1338's
// `upgradeTier`. The normalize() function below handles all four;
// backend tolerates them omitted on read.
const SCALAR_FIELDS: readonly string[] = [
  'State',
  'Gems',
  'ActiveBuffs',
  'upgradeTier',
  'statLevels',
  'Cosmetics',
];

let _player: Player | null = null;

// Bounded wait for the subscription's initial Overwrite. A missing row
// (first-time visitor) produces silence, not an error — the timeout IS the
// "not found" signal.
const READ_TIMEOUT_MS = 4_000;

export async function loadOrCreatePlayer(
  client: PlayServClient,
  userId: string,
): Promise<Player> {
  let existing: Record<string, unknown> | null = null;
  let handle: ListenHandle<Record<string, unknown>> | null = null;
  try {
    handle = client
      .model<Record<string, unknown>>('Player')
      .key(userId)
      .fields(...SCALAR_FIELDS)
      .with('Ship', undefined, SHIP_FIELDS)
      .with('Stats', undefined, STATS_FIELDS)
      .with('Profile', undefined, PROFILE_FIELDS)
      .listen();
    existing = await new Promise<Record<string, unknown> | null>((resolve) => {
      const timer = setTimeout(() => {
        resolve(null);
      }, READ_TIMEOUT_MS);
      handle!.wait('update', (event) => {
        clearTimeout(timer);
        resolve(event.new);
      });
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[player] subscription read threw, falling back to defaults (Arena still opens):',
      err,
    );
  } finally {
    // One-shot read: drop the handle either way. Live per-field updates
    // arrive via ArenaController's own Player listen + GameState snapshots.
    void handle?.stop();
  }

  if (existing !== null) {
    _player = normalize(existing);
    // eslint-disable-next-line no-console
    console.info(
      '[player] loaded Player via subscription push',
      JSON.parse(JSON.stringify(_player)) as Player,
    );
    return _player;
  }

  // First-time visitor (or silent backend) — run on defaults. PSV-1898:
  // no client-side create; the server upserts the row over its uplink
  // data channel on first spawn.
  _player = { ...DEFAULT_PLAYER };
  // eslint-disable-next-line no-console
  console.info(
    '[player] no Player row pushed — running on defaults (server creates the row on first spawn)',
    JSON.parse(JSON.stringify(_player)) as Player,
  );
  return _player;
}

export function getPlayer(): Player {
  if (_player === null) {
    throw new Error('Player not loaded. Call loadOrCreatePlayer() first.');
  }
  return _player;
}

// PSV-1898 — `persistPlayer` / `rawUpsertPlayer` removed: the server is
// authoritative for every Player field and persists via the uplink data
// channel; a client-side Overwrite here would clobber server writes.

/**
 * PSV-1337 — exported for Wave 1 unit tests (the only use of `normalize`
 * outside this module). All production call sites stay internal to
 * `loadOrCreatePlayer`.
 */
export function normalize(raw: Record<string, unknown>): Player {
  // Defensive: backend may evolve schema before the demo catches up. Missing
  // fields fall back to defaults rather than throwing.
  const ship = (raw.Ship as Record<string, unknown> | undefined) ?? {};
  const stats = (raw.Stats as Record<string, unknown> | undefined) ?? {};
  const profile = (raw.Profile as Record<string, unknown> | undefined) ?? {};
  return {
    Ship: {
      Class:
        typeof ship.Class === 'string' ? ship.Class : DEFAULT_PLAYER.Ship.Class,
      Weapon:
        typeof ship.Weapon === 'string'
          ? ship.Weapon
          : DEFAULT_PLAYER.Ship.Weapon,
    },
    Stats: {
      Kills: typeof stats.Kills === 'number' ? stats.Kills : 0,
      Deaths: typeof stats.Deaths === 'number' ? stats.Deaths : 0,
    },
    Profile: {
      FirstSeenAt:
        typeof profile.FirstSeenAt === 'string'
          ? profile.FirstSeenAt
          : DEFAULT_PLAYER.Profile.FirstSeenAt,
      LastSeenAt:
        typeof profile.LastSeenAt === 'string'
          ? profile.LastSeenAt
          : DEFAULT_PLAYER.Profile.LastSeenAt,
      SessionsCount:
        typeof profile.SessionsCount === 'number' ? profile.SessionsCount : 0,
    },
    State: typeof raw.State === 'string' ? raw.State : DEFAULT_PLAYER.State,
    // PSV-1337 — both default cleanly when absent (Gems → 0, ActiveBuffs → '').
    // Backend often omits empty-default fields from DataGet responses;
    // we tolerate that the same way Profile was tolerated in PSV-1393.
    Gems: typeof raw.Gems === 'number' ? raw.Gems : DEFAULT_PLAYER.Gems,
    ActiveBuffs:
      typeof raw.ActiveBuffs === 'string'
        ? raw.ActiveBuffs
        : DEFAULT_PLAYER.ActiveBuffs,
    // PSV-1338 — tier scalar. Falls back to 0 when omitted (first-time
    // player) or when the field is a non-number (schema drift).
    upgradeTier:
      typeof raw.upgradeTier === 'number' && Number.isFinite(raw.upgradeTier)
        ? raw.upgradeTier
        : DEFAULT_PLAYER.upgradeTier,
    // PSV-1727 — stat levels blob; tolerant of omission/drift like the rest.
    statLevels:
      typeof raw.statLevels === 'string'
        ? raw.statLevels
        : DEFAULT_PLAYER.statLevels,
    // PSV-1888 — cosmetics blob; tolerant of omission/drift like the rest.
    Cosmetics:
      typeof raw.Cosmetics === 'string'
        ? raw.Cosmetics
        : DEFAULT_PLAYER.Cosmetics,
  };
}
